import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'app/rootReducer'
import { useMutation } from '@apollo/client'
import { useWindowSize, Size } from 'hooks/useWindowSize'
import { setAuthentication } from 'reducers/authentication/authenticationSlice'
import { loadTheReduxStoreWithAuthenticationInfoResetHelper } from 'share/utils/utils'
import {
  CreatePasswordValidationType,
  ConfirmPasswordValidationType,
  createPasswordValidationTypeInitial,
  confirmPasswordValidationTypeInitial
} from 'share/utils/passwordUtils'
import {
  RESET_PASSWORD,
  ResetPasswordVariables,
  ResetPasswordResponse,
  ResetPasswordResponseData
} from 'share/mutations/account'
import GoBackHeader from 'components/organisms/GoBackHeader'
import ResetPasswordForm from 'components/organisms/ResetPasswordForm'
import { Redirect, useParams } from 'react-router-dom'
import './styles.scss'
import {
  ForgotPasswordData,
  setForgotPasswordData
} from 'reducers/forgotPassword/forgotpasswordSlice'

type ResetPasswordParams = {
  accountId: string
}

const ResetPassword = (): JSX.Element => {
  const authentication = useSelector((state: RootState) => state.authentication)
  const [placement, setPlacement] = useState<'right' | 'top'>('right')
  const [toDashboard, setToDashboard] = React.useState(false)

  const [createPasswordValidationResult, setCreatePasswordValidationResult] =
    useState<CreatePasswordValidationType>(() =>
      createPasswordValidationTypeInitial()
    )

  const [confirmPasswordValidationResult, setConfirmPasswordValidationResult] =
    useState<ConfirmPasswordValidationType>(() =>
      confirmPasswordValidationTypeInitial()
    )

  const [resetPassword] =
    useMutation<ResetPasswordResponse, ResetPasswordVariables>(RESET_PASSWORD)

  const size: Size = useWindowSize()
  const dispatch = useDispatch()
  const { accountId } = useParams<ResetPasswordParams>()

  useEffect(() => {
    if (accountId) {
      const convertedAccountId = parseInt(accountId)
      const forgotPwdData = {
        accountId: convertedAccountId
      } as ForgotPasswordData
      dispatch(setForgotPasswordData(forgotPwdData))
    }
  }, [])

  useEffect(() => {
    if (size.width && size.width <= 768) {
      setPlacement('top')
    } else {
      setPlacement('right')
    }
  }, [size])

  async function resetPasswordHelper(
    input: ResetPasswordVariables
  ): Promise<ResetPasswordResponseData> {
    const result: ResetPasswordResponseData = {
      success: false,
      error: null
    }

    try {
      const changePasswordResult = await resetPassword({
        variables: input
      })
      if (
        changePasswordResult.data?.authentication.confirmResetCredentials
          .idToken
      ) {
        loadTheReduxStoreWithAuthenticationInfo(changePasswordResult.data)
        result.success = true
      } else {
        result.success = false
        result.error = 'Reset password had an error. Please try again.'
      }
    } catch (e) {
      result.success = false
      result.error = 'Reset password had an error. Please try again.'
    }
    return result
  }

  function loadTheReduxStoreWithAuthenticationInfo(
    input: ResetPasswordResponse
  ): void {
    const authentication =
      loadTheReduxStoreWithAuthenticationInfoResetHelper(input)
    authentication.isAuthenticated = true
    dispatch(setAuthentication(authentication))
  }

  if (toDashboard === true) {
    if (authentication.account && authentication.account?.caseCount === 1) {
      return <Redirect to='/dashboard/case' />
    } else {
      return <Redirect to='/client' />
    }
  }

  return (
    <>
      <GoBackHeader text='Reset Password' />
      <div className='ui-reset-password px-4 mt-4 mt-lg-5'>
        <h2 className='mb-3'>Reset your password</h2>
        <p className='mb-4'>
          Keep your information safe and sound. We recommend that you memorize
          it and don't write it down.
        </p>
        <ResetPasswordForm
          placement={placement}
          createPasswordValidationResult={createPasswordValidationResult}
          setCreatePasswordValidationResult={setCreatePasswordValidationResult}
          confirmPasswordValidationResult={confirmPasswordValidationResult}
          setConfirmPasswordValidationResult={
            setConfirmPasswordValidationResult
          }
          resetPasswordHelper={resetPasswordHelper}
          setToDashboard={setToDashboard}
        />
      </div>
    </>
  )
}

export default ResetPassword
